import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCityContext } from '../../context/CityContext';
import { useNotificationsContext } from '../../context/NotificationsContext';
import TopicCard from '../../components/TopicCard';
import Modal from '../../components/UI/Modal';
import SubscriptionForm from '../../components/SubscriptionForm';
import SearchBar from '../../components/UI/SearchBar';
import './CityPortal.css';

export default function CityPortal() {
  const { citySlug } = useParams();
  const { cityList, activateCity } = useCityContext();
  const { getSubscriptionsForCity, isSubscribed, subscribe, unsubscribe } = useNotificationsContext();

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [modalTopic, setModalTopic] = useState(null);

  const city = cityList.find((c) => c.slug === citySlug);

  useEffect(() => {
    if (city) {
      activateCity(city.slug);
      document.title = `Notify ${city.name} — City Alerts`;
    }
  }, [city, activateCity]);

  const categories = useMemo(() => {
    if (!city) return ['All'];
    const cats = [...new Set(city.topics.map((t) => t.category))];
    return ['All', ...cats];
  }, [city]);

  const filteredTopics = useMemo(() => {
    if (!city) return [];
    return city.topics.filter((t) => {
      const matchesSearch =
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.description.toLowerCase().includes(search.toLowerCase()) ||
        t.category.toLowerCase().includes(search.toLowerCase());
      const matchesCat = activeCategory === 'All' || t.category === activeCategory;
      return matchesSearch && matchesCat;
    });
  }, [city, search, activeCategory]);

  const subscriptions = getSubscriptionsForCity(citySlug);

  if (!city) {
    return (
      <div className="portal-not-found">
        <div className="portal-not-found-inner">
          <div className="portal-nf-icon">🏙️</div>
          <h2>City not found</h2>
          <p>We don't have a portal for <strong>"{citySlug}"</strong> yet.</p>
          <Link to="/" className="portal-nf-link">← Back to all cities</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="city-portal">
      {/* Hero */}
      <section className="portal-hero">
        <div className="portal-hero-inner">
          <div className="portal-hero-mark">{city.name.charAt(0)}</div>
          <div className="portal-hero-content">
            <div className="portal-hero-eyebrow">
              City Notification Portal · {city.name}, {city.state}
            </div>
            <h1 className="portal-hero-title">Notify {city.name}</h1>
            <p className="portal-hero-sub">{city.tagline}</p>
          </div>
          {subscriptions.length > 0 && (
            <Link to={`/${citySlug}/my-alerts`} className="portal-hero-sub-btn">
              📋 {subscriptions.length} active subscription{subscriptions.length !== 1 ? 's' : ''}
            </Link>
          )}
        </div>
        <div className="portal-hero-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* Filters */}
      <section className="portal-filters">
        <div className="portal-filters-inner">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={`Search ${city.name} alerts…`}
          />
          <div className="portal-category-pills">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`category-pill${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Topic grid */}
      <section className="portal-topics">
        <div className="portal-topics-inner">
          {filteredTopics.length > 0 ? (
            <>
              <div className="portal-topics-header">
                <span className="portal-topics-count">
                  {filteredTopics.length} alert type{filteredTopics.length !== 1 ? 's' : ''}
                  {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
                </span>
              </div>
              <div className="portal-topics-grid">
                {filteredTopics.map((topic) => (
                  <TopicCard
                    key={topic.id}
                    topic={topic}
                    isSubscribed={isSubscribed(citySlug, topic.id)}
                    onSubscribe={() => setModalTopic(topic)}
                    onUnsubscribe={() => setModalTopic(topic)}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="portal-empty">
              <div className="portal-empty-icon">🔍</div>
              <h3>No alerts found</h3>
              <p>Try a different search term or category.</p>
              <button className="portal-empty-reset" onClick={() => { setSearch(''); setActiveCategory('All'); }}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Subscription modal */}
      {modalTopic && (
        <Modal
          isOpen={!!modalTopic}
          onClose={() => setModalTopic(null)}
          title={isSubscribed(citySlug, modalTopic.id) ? 'Manage Subscription' : 'Subscribe to Alert'}
          size="md"
        >
          <SubscriptionForm
            topic={modalTopic}
            citySlug={citySlug}
            existingSubscription={
              isSubscribed(citySlug, modalTopic.id)
                ? subscriptions.find((s) => s.topicId === modalTopic.id)
                : null
            }
            onSubmit={({ methods, contact, address }) => {
              subscribe(citySlug, modalTopic.id, { methods, contact, address });
            }}
            onUnsubscribe={() => {
              unsubscribe(citySlug, modalTopic.id);
              setModalTopic(null);
            }}
            onCancel={() => setModalTopic(null)}
          />
        </Modal>
      )}
    </div>
  );
}
